import React, { useState, useMemo } from 'react';
import { Table, Pagination, Spinner } from 'react-bootstrap';
import { FaExclamationTriangle, FaInbox, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';
import { formatarMonetario } from '~/Utils/Helpers';
import { ITENS_POR_PAGINA } from './constantes';
import { getLabelFaixa } from './utils';

var COLUNAS = [
  { campo: 'codigo', label: 'Código', tipo: 'numero' },
  { campo: 'nome', label: 'Nome', tipo: 'texto' },
  { campo: 'cpf', label: 'CPF', tipo: 'texto' },
  { campo: 'renda_pessoal', label: 'Renda Pessoal', tipo: 'numero' },
  { campo: 'renda_familiar', label: 'Renda Familiar', tipo: 'numero' },
  { campo: '_renda_efetiva', label: 'Renda Efetiva', tipo: 'numero' },
  { campo: '_faixa', label: 'Faixa', tipo: 'texto' },
  { campo: 'interesse_social', label: 'Int. Social', tipo: 'booleano' },
  { campo: 'etapa', label: 'Etapa', tipo: 'texto' },
  { campo: 'documentacao_completa', label: 'Doc. Completa', tipo: 'booleano' },
  { campo: '_pasta_completa_sem_renda', label: '\u26A0', tipo: 'booleano', title: 'Pasta completa sem renda informada' },
  { campo: 'nome_corretor', label: 'Corretor', tipo: 'texto' },
];

function comparar(a, b, campo, tipo) {
  var valA = a[campo];
  var valB = b[campo];

  if (valA == null && valB == null) return 0;
  if (valA == null) return 1;
  if (valB == null) return -1;

  if (tipo === 'numero') {
    return (Number(valA) || 0) - (Number(valB) || 0);
  }
  if (tipo === 'booleano') {
    return (valA ? 1 : 0) - (valB ? 1 : 0);
  }
  return String(valA).localeCompare(String(valB), 'pt-BR', { sensitivity: 'base' });
}

var TabelaResultados = function(props) {
  var dados = props.dados;
  var isLoading = props.isLoading;
  var paginaAtual = props.paginaAtual;
  var setPaginaAtual = props.setPaginaAtual;

  var stOrdenacao = useState({ campo: null, direcao: 'asc' });
  var ordenacao = stOrdenacao[0];
  var setOrdenacao = stOrdenacao[1];

  var dadosOrdenados = useMemo(function() {
    if (!dados || !ordenacao.campo) return dados || [];
    var campo = ordenacao.campo;
    var col = COLUNAS.find(function(c) { return c.campo === campo; });
    var tipo = col ? col.tipo : 'texto';
    var dir = ordenacao.direcao === 'asc' ? 1 : -1;

    var copia = dados.slice();
    copia.sort(function(a, b) {
      return comparar(a, b, campo, tipo) * dir;
    });
    return copia;
  }, [dados, ordenacao]);

  var totalRegistros = dadosOrdenados.length;
  var totalPaginas = Math.ceil(totalRegistros / ITENS_POR_PAGINA);
  var inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
  var dadosPaginados = dadosOrdenados.slice(inicio, inicio + ITENS_POR_PAGINA);

  function handleOrdenar(campo) {
    setOrdenacao(function(prev) {
      if (prev.campo === campo) {
        return { campo: campo, direcao: prev.direcao === 'asc' ? 'desc' : 'asc' };
      }
      return { campo: campo, direcao: 'asc' };
    });
    setPaginaAtual(1);
  }

  function renderIconeSort(campo) {
    if (ordenacao.campo !== campo) {
      return <FaSort style={{ opacity: 0.3, marginLeft: 4 }} />;
    }
    if (ordenacao.direcao === 'asc') {
      return <FaSortUp style={{ marginLeft: 4 }} />;
    }
    return <FaSortDown style={{ marginLeft: 4 }} />;
  }

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Carregando relatório...</p>
      </div>
    );
  }

  if (!dados || dados.length === 0) {
    return (
      <div className="text-center p-5 text-muted">
        <FaInbox size={40} />
        <p className="mt-2">Nenhum registro encontrado</p>
      </div>
    );
  }

  function renderPaginacao() {
    if (totalPaginas <= 1) return null;

    var items = [];
    var maxBotoes = 5;
    var metade = Math.floor(maxBotoes / 2);
    var paginaInicial = Math.max(1, paginaAtual - metade);
    var paginaFinal = Math.min(totalPaginas, paginaInicial + maxBotoes - 1);

    if (paginaFinal - paginaInicial < maxBotoes - 1) {
      paginaInicial = Math.max(1, paginaFinal - maxBotoes + 1);
    }

    items.push(
      <Pagination.First
        key="first"
        disabled={paginaAtual === 1}
        onClick={function() { setPaginaAtual(1); }}
      />
    );
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={paginaAtual === 1}
        onClick={function() { setPaginaAtual(paginaAtual - 1); }}
      />
    );

    for (var i = paginaInicial; i <= paginaFinal; i++) {
      (function(pagina) {
        items.push(
          <Pagination.Item
            key={pagina}
            active={pagina === paginaAtual}
            onClick={function() { setPaginaAtual(pagina); }}
          >
            {pagina}
          </Pagination.Item>
        );
      })(i);
    }

    items.push(
      <Pagination.Next
        key="next"
        disabled={paginaAtual === totalPaginas}
        onClick={function() { setPaginaAtual(paginaAtual + 1); }}
      />
    );
    items.push(
      <Pagination.Last
        key="last"
        disabled={paginaAtual === totalPaginas}
        onClick={function() { setPaginaAtual(totalPaginas); }}
      />
    );

    var registroInicial = inicio + 1;
    var registroFinal = Math.min(inicio + ITENS_POR_PAGINA, totalRegistros);

    return (
      <div className="d-flex justify-content-between align-items-center mt-2">
        <small className="text-muted">
          Exibindo {registroInicial} a {registroFinal} de {totalRegistros} registros
        </small>
        <Pagination size="sm" className="mb-0">
          {items}
        </Pagination>
      </div>
    );
  }

  return (
    <div className="eq-tabela-wrapper">
      <div className="table-responsive">
        <Table striped hover size="sm" style={{ fontSize: 12 }}>
          <thead>
            <tr>
              {COLUNAS.map(function(col) {
                return (
                  <th
                    key={col.campo}
                    title={col.title || col.label}
                    onClick={function() { handleOrdenar(col.campo); }}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    {col.label}
                    {renderIconeSort(col.campo)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {dadosPaginados.map(function(d, idx) {
              return (
                <tr key={idx}>
                  <td>{d.codigo}</td>
                  <td>{d.nome}</td>
                  <td>{d.cpf}</td>
                  <td>{formatarMonetario(d.renda_pessoal)}</td>
                  <td>{formatarMonetario(d.renda_familiar)}</td>
                  <td>{formatarMonetario(d._renda_efetiva)}</td>
                  <td>{getLabelFaixa(d._faixa)}</td>
                  <td>{d.interesse_social ? 'Sim' : 'Não'}</td>
                  <td>{d.etapa}</td>
                  <td>{d.documentacao_completa ? 'Sim' : 'Não'}</td>
                  <td className="text-center">
                    {d._pasta_completa_sem_renda && (
                      <FaExclamationTriangle
                        color="#ffc107"
                        title="Pasta completa sem renda informada"
                      />
                    )}
                  </td>
                  <td>{d.nome_corretor}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
      {renderPaginacao()}
    </div>
  );
};

export default TabelaResultados;
